import React, { useState, useEffect, useCallback } from 'react';
import {
  X, User, Mail, Phone, MapPin, Calendar, Clock, MessageSquare,
  Shield, AlertTriangle, Ban, CheckCircle, Sparkles, FileDown,
  Pin, PinOff, Star, History, Loader2, Award, Bot, RefreshCw,
  TrendingUp, BadgeCheck, UserX, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CustomerProfilePanel — Comprehensive 360° customer view for staff.
 * Slide-in panel from right side. Shows customer info, past chats, notes,
 * AI summary, block status, and quick actions.
 */
const CustomerProfilePanel = ({ sessionId, getAuthHeaders, currentUser, onClose, onOpenSession }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const fetchProfile = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/livechat/customer-profile/${sessionId}`, { headers });
      setProfile(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [sessionId, getAuthHeaders]);

  const fetchCachedSummary = useCallback(async () => {
    if (!sessionId) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/livechat/ai-summary-cached/${sessionId}`, { headers });
      if (res.data?.summary) setAiSummary(res.data);
    } catch {
      // silent — no cached summary yet
    }
  }, [sessionId, getAuthHeaders]);

  useEffect(() => {
    fetchProfile();
    fetchCachedSummary();
    setActiveTab('overview');
  }, [sessionId, fetchProfile, fetchCachedSummary]);

  const generateAISummary = async () => {
    setAiSummaryLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/livechat/ai-summary/${sessionId}`, { headers });
      setAiSummary(res.data);
      toast.success('Summary generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Summary failed');
    } finally {
      setAiSummaryLoading(false);
    }
  };

  const togglePin = async () => {
    try {
      const headers = getAuthHeaders();
      if (profile?.current_session?.pinned) {
        await axios.delete(`${BACKEND_URL}/api/admin/livechat/pin/${sessionId}`, { headers });
        toast.success('Unpinned');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/livechat/pin/${sessionId}`, {}, { headers });
        toast.success('Pinned to top');
      }
      fetchProfile();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const exportTranscript = () => {
    const headers = getAuthHeaders();
    const token = headers?.Authorization?.replace('Bearer ', '');
    // Open in new tab — endpoint returns HTML. Token via query param fallback:
    const url = `${BACKEND_URL}/api/admin/livechat/export/${sessionId}`;
    // We need auth — fetch then open as blob
    fetch(url, { headers })
      .then(r => r.text())
      .then(html => {
        const blob = new Blob([html], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        const win = window.open(blobUrl, '_blank');
        if (!win) {
          toast.error('Pop-up blocked — allow pop-ups for this site');
        } else {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        }
      })
      .catch(() => toast.error('Export failed'));
  };

  const blockCustomer = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/livechat/block/${sessionId}`,
        { reason: blockReason }, { headers });
      toast.success('Customer blocked from live chat');
      setShowBlockConfirm(false);
      setBlockReason('');
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Block failed');
    }
  };

  const unblockCustomer = async () => {
    if (!window.confirm('Lift the live-chat block on this customer?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/livechat/unblock/${sessionId}`, {}, { headers });
      toast.success('Customer unblocked');
      fetchProfile();
    } catch (err) {
      toast.error('Unblock failed');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  const formatDateShort = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
  };

  if (!sessionId) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end pointer-events-none" data-testid="customer-profile-panel">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
        onClick={onClose}
        data-testid="customer-profile-backdrop"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-md bg-[#0A0A12] border-l border-white/10 shadow-2xl pointer-events-auto overflow-y-auto"
        style={{ animation: 'slideInRight 0.3s ease-out' }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0A0A12]/95 backdrop-blur-md border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-400/30 flex items-center justify-center">
              <User className="w-4 h-4 text-cyan-300" />
            </div>
            <div>
              <h2 className="text-white font-medium tracking-tight">Customer Profile</h2>
              <p className="text-[10px] uppercase tracking-[0.15em] text-white/40">360° view</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-profile-btn"
            className="p-1.5 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin mb-3" />
            <p className="text-sm text-white/40">Loading customer data…</p>
          </div>
        ) : profile ? (
          <>
            {/* Customer Identity Block */}
            <div className="px-5 py-5 border-b border-white/[0.06]">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-violet-500/30 border border-white/10 flex items-center justify-center text-2xl font-light text-white">
                  {(profile.customer.name || 'C').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-white tracking-tight truncate" data-testid="customer-name">
                    {profile.customer.name || 'Anonymous'}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-1">
                    {profile.customer.is_registered_user && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                        <BadgeCheck className="w-2.5 h-2.5" /> Verified
                      </span>
                    )}
                    {profile.customer.is_staff_role && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-300 border border-amber-500/20">
                        <Award className="w-2.5 h-2.5" /> Staff
                      </span>
                    )}
                    {profile.block.is_blocked && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-500/10 text-red-300 border border-red-500/20">
                        <Ban className="w-2.5 h-2.5" /> Blocked
                      </span>
                    )}
                    {profile.current_session.user_online && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact rows */}
              <div className="space-y-1.5 text-sm">
                <InfoRow icon={Mail} label="Email" value={profile.customer.email} testid="profile-email" copyable />
                <InfoRow icon={Phone} label="Phone" value={profile.customer.phone} testid="profile-phone" />
                <InfoRow icon={MapPin} label="Location" value={profile.customer.location} testid="profile-location" />
                <InfoRow icon={Calendar} label="Signup" value={formatDateShort(profile.customer.signup_date)} testid="profile-signup" />
                <InfoRow icon={Clock} label="Last login" value={formatDateShort(profile.customer.last_login)} testid="profile-lastlogin" />
              </div>

              {profile.customer.bio && (
                <div className="mt-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-1">Bio</p>
                  <p className="text-xs text-white/60 leading-relaxed">{profile.customer.bio}</p>
                </div>
              )}
            </div>

            {/* Stats Strip */}
            <div className="grid grid-cols-3 divide-x divide-white/[0.06] border-b border-white/[0.06]">
              <StatCell label="Chats" value={profile.stats.total_chats} icon={MessageSquare} color="cyan" />
              <StatCell label="Messages" value={profile.stats.total_messages} icon={TrendingUp} color="violet" />
              <StatCell label="Resolved" value={profile.stats.resolved_count} icon={CheckCircle} color="emerald" />
            </div>

            {/* Action Buttons */}
            <div className="px-5 py-3 border-b border-white/[0.06] grid grid-cols-2 gap-2">
              <ActionBtn
                onClick={togglePin}
                icon={profile.current_session.pinned ? PinOff : Pin}
                label={profile.current_session.pinned ? 'Unpin' : 'Pin chat'}
                testid="profile-pin-btn"
              />
              <ActionBtn onClick={exportTranscript} icon={FileDown} label="Export transcript" testid="profile-export-btn" />
              <ActionBtn
                onClick={generateAISummary}
                icon={aiSummaryLoading ? Loader2 : Sparkles}
                label={aiSummaryLoading ? 'Summarising…' : 'AI summary'}
                testid="profile-ai-summary-btn"
                disabled={aiSummaryLoading}
                spin={aiSummaryLoading}
              />
              {profile.block.is_blocked ? (
                <ActionBtn onClick={unblockCustomer} icon={CheckCircle} label="Unblock" testid="profile-unblock-btn" variant="success" />
              ) : (
                <ActionBtn onClick={() => setShowBlockConfirm(true)} icon={Ban} label="Block" testid="profile-block-btn" variant="danger" />
              )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-white/[0.06]">
              <TabBtn active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} label="Overview" testid="tab-overview" />
              <TabBtn
                active={activeTab === 'history'}
                onClick={() => setActiveTab('history')}
                label={`Past chats (${profile.previous_sessions.length})`}
                testid="tab-history"
              />
              <TabBtn active={activeTab === 'csat'} onClick={() => setActiveTab('csat')} label="CSAT" testid="tab-csat" />
            </div>

            {/* Tab content */}
            <div className="px-5 py-4">
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {profile.block.is_blocked && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="block-banner">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-red-300">Customer Blocked</p>
                          <p className="text-[11px] text-red-300/70 mt-0.5">
                            Reason: {profile.block.reason || '—'} • by {profile.block.blocked_by} • {formatDateShort(profile.block.blocked_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {aiSummary?.summary && (
                    <div className="p-3 rounded-lg bg-gradient-to-br from-violet-500/[0.06] to-cyan-500/[0.04] border border-violet-500/20" data-testid="ai-summary-box">
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className="w-3.5 h-3.5 text-violet-300" />
                        <p className="text-[10px] uppercase tracking-[0.15em] text-violet-300 font-medium">AI Summary</p>
                        {aiSummary.generated_at && (
                          <p className="text-[10px] text-white/30 ml-auto">{formatDateShort(aiSummary.generated_at)}</p>
                        )}
                      </div>
                      <p className="text-xs text-white/70 leading-relaxed whitespace-pre-wrap">{aiSummary.summary}</p>
                    </div>
                  )}

                  {/* Tags */}
                  {profile.current_session.tags?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-2">Tags</p>
                      <div className="flex flex-wrap gap-1.5">
                        {profile.current_session.tags.map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 rounded text-[11px] bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Session meta */}
                  <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05] text-xs space-y-1.5">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-1">Current session</p>
                    <div className="flex justify-between"><span className="text-white/40">Status</span><span className="text-white/80">{profile.current_session.status}</span></div>
                    <div className="flex justify-between"><span className="text-white/40">Started</span><span className="text-white/80">{formatDate(profile.current_session.created_at)}</span></div>
                    <div className="flex justify-between"><span className="text-white/40">Agent</span><span className="text-white/80">{profile.current_session.claimed_by_name || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-white/40">Last seen</span><span className="text-white/80">{formatDate(profile.current_session.user_last_seen)}</span></div>
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <div className="space-y-2" data-testid="past-chats-list">
                  {profile.previous_sessions.length === 0 ? (
                    <p className="text-xs text-white/40 text-center py-6">No previous chats with this customer.</p>
                  ) : (
                    profile.previous_sessions.map((s) => (
                      <button
                        key={s.session_id}
                        onClick={() => onOpenSession?.(s.session_id)}
                        data-testid={`past-chat-${s.session_id}`}
                        className="w-full text-left p-3 rounded-lg bg-white/[0.02] border border-white/[0.05] hover:border-cyan-400/30 hover:bg-white/[0.04] transition-all group"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <History className="w-3 h-3 text-white/40 group-hover:text-cyan-300" />
                            <p className="text-xs text-white/80 font-medium">{formatDateShort(s.created_at)}</p>
                          </div>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                            s.status === 'resolved' ? 'bg-emerald-500/10 text-emerald-300' :
                            s.status === 'claimed' ? 'bg-cyan-500/10 text-cyan-300' :
                            'bg-amber-500/10 text-amber-300'
                          }`}>
                            {s.status}
                          </span>
                        </div>
                        <p className="text-[11px] text-white/40">
                          Agent: {s.claimed_by_name || '—'}
                          {s.resolved_at && ` • Resolved ${formatDateShort(s.resolved_at)}`}
                        </p>
                        {s.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {s.tags.map((t, i) => (
                              <span key={i} className="text-[10px] text-cyan-300/70">#{t}</span>
                            ))}
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'csat' && (
                <div className="space-y-2" data-testid="csat-history">
                  {profile.csat_history.length === 0 ? (
                    <p className="text-xs text-white/40 text-center py-6">No customer satisfaction ratings yet.</p>
                  ) : (
                    profile.csat_history.map((c, i) => (
                      <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star
                                key={n}
                                className={`w-3.5 h-3.5 ${n <= c.rating ? 'text-amber-400 fill-amber-400' : 'text-white/15'}`}
                              />
                            ))}
                          </div>
                          <p className="text-[10px] text-white/40">{formatDateShort(c.rated_at)}</p>
                        </div>
                        {c.feedback && (
                          <p className="text-xs text-white/60 italic mt-1">{`"${c.feedback}"`}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Block confirmation modal */}
            {showBlockConfirm && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/80 backdrop-blur-sm px-5">
                <div className="w-full max-w-sm bg-[#0F0F1A] border border-red-500/30 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                      <UserX className="w-4 h-4 text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Block customer</h3>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-red-400/70">irreversible until lifted</p>
                    </div>
                  </div>
                  <p className="text-xs text-white/60 mb-3 leading-relaxed">
                    This will permanently block <span className="text-white">{profile.customer.email}</span> from starting any new live chats and resolve the current session.
                  </p>
                  <textarea
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="Reason (recorded in audit log)"
                    className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-xs placeholder-white/30 focus:outline-none focus:border-red-400/40 mb-3 resize-none"
                    rows={2}
                    data-testid="block-reason-input"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowBlockConfirm(false); setBlockReason(''); }}
                      className="flex-1 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-xs transition-colors"
                      data-testid="block-cancel-btn"
                    >Cancel</button>
                    <button
                      onClick={blockCustomer}
                      className="flex-1 px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 text-xs font-medium transition-colors"
                      data-testid="block-confirm-btn"
                    >Confirm Block</button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-24 text-center">
            <p className="text-sm text-white/40">Failed to load profile</p>
            <button onClick={fetchProfile} className="mt-3 text-xs text-cyan-400 hover:underline">
              <RefreshCw className="w-3 h-3 inline mr-1" /> Retry
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ============================================================
// Helper sub-components
// ============================================================
const InfoRow = ({ icon: Icon, label, value, testid, copyable }) => {
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };
  return (
    <div className="flex items-center gap-2.5" data-testid={testid}>
      <Icon className="w-3.5 h-3.5 text-white/30 shrink-0" />
      <span className="text-[10px] uppercase tracking-[0.15em] text-white/30 w-14 shrink-0">{label}</span>
      <span className={`flex-1 text-xs truncate ${value ? 'text-white/80' : 'text-white/25 italic'}`}>
        {value || 'Not provided'}
      </span>
      {copyable && value && (
        <button onClick={copy} className="text-white/30 hover:text-cyan-300 transition-colors p-0.5">
          <FileText className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const StatCell = ({ label, value, icon: Icon, color }) => (
  <div className="flex flex-col items-center justify-center py-3.5">
    <Icon className={`w-3.5 h-3.5 mb-1 ${
      color === 'cyan' ? 'text-cyan-400' :
      color === 'violet' ? 'text-violet-400' :
      'text-emerald-400'
    }`} />
    <p className="text-xl font-light text-white tracking-tight">{value}</p>
    <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mt-0.5">{label}</p>
  </div>
);

const ActionBtn = ({ onClick, icon: Icon, label, testid, variant, disabled, spin }) => {
  const styles = variant === 'danger'
    ? 'bg-red-500/[0.08] hover:bg-red-500/15 text-red-300 border-red-500/20'
    : variant === 'success'
    ? 'bg-emerald-500/[0.08] hover:bg-emerald-500/15 text-emerald-300 border-emerald-500/20'
    : 'bg-white/[0.03] hover:bg-white/[0.06] text-white/80 border-white/10';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg border text-[11px] font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${styles}`}
    >
      <Icon className={`w-3.5 h-3.5 ${spin ? 'animate-spin' : ''}`} />
      {label}
    </button>
  );
};

const TabBtn = ({ active, onClick, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 px-3 py-2.5 text-[11px] font-medium uppercase tracking-[0.1em] transition-all border-b-2 ${
      active ? 'text-cyan-300 border-cyan-400' : 'text-white/40 border-transparent hover:text-white/70'
    }`}
  >
    {label}
  </button>
);

export default CustomerProfilePanel;
